import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
} from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import type { ModuleMeta } from "../../content/catalog";
import type { ProgressData } from "../../store/progress";
import CourseDirectory from "./CourseDirectory";
import "./courseSidebar.css";

// Keep the shell's 1279px drawer breakpoint and its modal state in sync.
const compactQuery = "(max-width: 1279px)";

export default function CourseSidebar({
  modules,
  progress,
  open,
  setOpen,
  opener,
}: {
  modules: readonly ModuleMeta[];
  progress: ProgressData;
  open: boolean;
  setOpen: (value: boolean) => void;
  opener: RefObject<HTMLButtonElement | null>;
}) {
  const [compact, setCompact] = useState(
    () => matchMedia(compactQuery).matches,
  );
  const [search, setSearch] = useState("");
  const dialog = useRef<HTMLDialogElement>(null);
  const sidebar = useRef<HTMLElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const resizeFocus = useRef<"opener" | "search">(undefined);
  const focusFrame = useRef<number>(undefined);
  const location = useLocation();
  const previousLocation = useRef(location.key);

  useEffect(() => {
    const query = matchMedia(compactQuery);
    const change = () => {
      const focused = document.activeElement;
      if (
        dialog.current?.contains(focused) ||
        sidebar.current?.contains(focused)
      )
        resizeFocus.current = query.matches ? "opener" : "search";
      dialog.current?.close();
      setOpen(false);
      setCompact(query.matches);
    };
    query.addEventListener("change", change);
    return () => query.removeEventListener("change", change);
  }, [setOpen]);

  useLayoutEffect(() => {
    const node = dialog.current;
    if (compact && open && node && !node.open) {
      // Native modality makes the entire rest of the document inert, including
      // embedded previews, the skip link and controls outside studio-main.
      node.showModal();
      closeButton.current?.focus();
    }
    return () => {
      if (node?.open) node.close();
    };
  }, [compact, open]);

  useLayoutEffect(() => {
    if (resizeFocus.current === "opener") opener.current?.focus();
    if (resizeFocus.current === "search")
      sidebar.current?.querySelector<HTMLInputElement>("input")?.focus();
    resizeFocus.current = undefined;
  }, [compact, opener]);

  useEffect(() => {
    if (previousLocation.current !== location.key) {
      previousLocation.current = location.key;
      if (open) {
        dialog.current?.close();
        setOpen(false);
        focusFrame.current = requestAnimationFrame(() =>
          document.getElementById("learning-main")?.focus(),
        );
      }
    }
  }, [location.key, open, setOpen]);
  useEffect(
    () => () => {
      if (focusFrame.current !== undefined)
        cancelAnimationFrame(focusFrame.current);
    },
    [],
  );

  const dismiss = () => {
    dialog.current?.close();
    setOpen(false);
    opener.current?.focus();
  };
  const navigate = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    dialog.current?.close();
    setOpen(false);
    if (focusFrame.current !== undefined)
      cancelAnimationFrame(focusFrame.current);
    // Also handles selecting the current lesson, where the path needn't change.
    focusFrame.current = requestAnimationFrame(() =>
      document.getElementById("learning-main")?.focus(),
    );
  };
  const containTab = (event: KeyboardEvent<HTMLDialogElement>) => {
    // Parent learning shortcuts must not run while typing in the modal search.
    event.stopPropagation();
    if (event.key !== "Tab") return;
    const controls = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        "a[href], button, input, select, textarea, summary, [tabindex]",
      ),
    ).filter(
      (node) =>
        node.tabIndex >= 0 &&
        !node.matches(":disabled") &&
        !node.closest("[inert]") &&
        node.getClientRects().length > 0 &&
        getComputedStyle(node).visibility === "visible",
    );
    const first = controls[0];
    const last = controls.at(-1);
    if (
      (event.shiftKey && document.activeElement === first) ||
      (!event.shiftKey && document.activeElement === last)
    ) {
      event.preventDefault();
      (event.shiftKey ? last : first)?.focus();
    }
  };
  const content = (
    <CourseDirectory
      modules={modules}
      progress={progress}
      search={search}
      setSearch={setSearch}
      onNavigate={navigate}
    />
  );
  return compact ? (
    <dialog
      ref={dialog}
      id="course-navigation"
      className="course-sidebar course-drawer"
      aria-labelledby="course-drawer-title"
      aria-modal="true"
      onCancel={(event) => {
        event.preventDefault();
        dismiss();
      }}
      onKeyDown={containTab}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        )
          dismiss();
      }}
    >
      <header className="course-drawer-header">
        <h2 id="course-drawer-title">课程目录</h2>
        <button ref={closeButton} aria-label="关闭课程目录" onClick={dismiss}>
          <X size={18} />
        </button>
      </header>
      {content}
    </dialog>
  ) : (
    <aside ref={sidebar} id="course-navigation" className="course-sidebar">
      {content}
    </aside>
  );
}
